import { Alert, Box, Button, ColumnLayout, Link, SpaceBetween } from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { SortableList } from '../v2-common/SortableList';
import {
  decreaseCountInCountMap,
  FormErrorType,
  keyOfPublication,
  PublicationItem,
  PublicationType,
  reorderVisiblePublications,
  updateCountMap,
} from './APGLUserProfileUtils';
import PublicationSelection from './PublicationSelection';
import * as UtilsApi from '../../api/UtilsApi';

interface Props {
  contentNameMap: Map<string, string>;
  publicationType: PublicationType;
  selectedPubs: PublicationItem[];
  allPubs: string[];
  lastId: number; // Largest ID number that has been used for items in PublicationList. It gets increased by 1 for each newly added item.
  handleSelectionsChanged: (pubs: PublicationItem[]) => void;
  handleLastIdUpdated: (id: number) => void;
  handleCountMapUpdated: (map: Map<string, number>, type: FormErrorType) => void;
}

export const PublicationList: React.FunctionComponent<Props> = (props: Props) => {
  const {
    contentNameMap,
    publicationType,
    selectedPubs,
    allPubs,
    lastId,
    handleSelectionsChanged,
    handleLastIdUpdated,
    handleCountMapUpdated,
  } = props;

  const [contents, setContents] = useState<PublicationItem[]>(selectedPubs);
  const [latestId, setLatestId] = useState<number>(lastId);
  const [contentCountMap, setContentCountMap] = useState<Map<string, number>>(() => {
    const initCountMap = new Map<string, number>();
    selectedPubs.forEach((pub) => {
      const { contentId } = pub;
      // @ts-expect-error TS2532 - strict migration
      const pubCount = initCountMap.has(contentId) ? initCountMap.get(contentId) + 1 : 1;
      initCountMap.set(contentId, pubCount);
    });
    return initCountMap;
  }); // contentId->count to track duplication
  // for expand and collapse
  const [contentsToShow, setContentsToShow] = useState<PublicationItem[]>(() => {
    if (selectedPubs.length > UtilsApi.defaultNumberOfContentsToShow) {
      return selectedPubs.slice(0, UtilsApi.defaultNumberOfContentsToShow);
    } else {
      return selectedPubs;
    }
  });
  const [numberOfContentsToShow, setNumberOfContentsToShow] = useState<number>(
    selectedPubs.length > UtilsApi.defaultNumberOfContentsToShow
      ? UtilsApi.defaultNumberOfContentsToShow
      : selectedPubs.length,
  );
  const [alert, setAlert] = useState<string>('');

  const onSelectionChanged = (pub: PublicationItem) => {
    const currentContents = [...contents];
    const index = currentContents.findIndex((content) => content.id === pub.id);
    // @ts-expect-error TS2532 - strict migration
    const oldContentId = currentContents[index].contentId;
    const newContentId = pub.contentId;
    if (oldContentId !== newContentId) {
      // content has changed, we need to updated content->count map
      updateCountMap(oldContentId, newContentId, contentCountMap);
    }
    currentContents[index] = pub;
    setContents(currentContents);
    setContentsToShow(currentContents.slice(0, numberOfContentsToShow));
    handleSelectionsChanged(currentContents);
    setContentCountMap(contentCountMap);
    handleCountMapUpdated(
      contentCountMap,
      publicationType === PublicationType.AuthoredContent
        ? FormErrorType.PubDuplicationError
        : FormErrorType.ReviewDuplicationError,
    );
  };

  const onDeleteSelection = (id: number) => {
    setAlert('');
    // @ts-expect-error TS2532 - strict migration
    const deletedContentId = contents.find((c) => c.id === id).contentId;
    decreaseCountInCountMap(deletedContentId, contentCountMap);
    const currentContents = contents.filter((content) => content.id !== id);
    setContents(currentContents);
    setContentsToShow(currentContents.slice(0, numberOfContentsToShow - 1));
    setNumberOfContentsToShow(numberOfContentsToShow - 1);
    handleSelectionsChanged(currentContents);
    setContentCountMap(contentCountMap);
    handleCountMapUpdated(
      contentCountMap,
      publicationType === PublicationType.AuthoredContent
        ? FormErrorType.PubDuplicationError
        : FormErrorType.ReviewDuplicationError,
    );
  };

  const onReorder = (orderedKeys: readonly string[]) => {
    // Only the visible prefix is sortable; hidden items keep their positions after it.
    const newContents = reorderVisiblePublications(contents, contentsToShow.length, orderedKeys);
    setContents(newContents);
    setContentsToShow(newContents.slice(0, numberOfContentsToShow));
    handleSelectionsChanged(newContents);
  };

  const numberOfContentsToAdd =
    contents.length - numberOfContentsToShow >= UtilsApi.numberOfContentsToAdd
      ? UtilsApi.numberOfContentsToAdd
      : contents.length - numberOfContentsToShow;

  const handleShowMoreClick = () => {
    const newNumberOfContentsToShow = numberOfContentsToShow + numberOfContentsToAdd;
    setContentsToShow(contents.slice(0, newNumberOfContentsToShow));
    setNumberOfContentsToShow(newNumberOfContentsToShow);
  };

  const handleShowLessClick = () => {
    const newNumberOfContentsToShow = UtilsApi.defaultNumberOfContentsToShow;
    setContentsToShow(contents.slice(0, newNumberOfContentsToShow));
    setNumberOfContentsToShow(newNumberOfContentsToShow);
  };

  const showMoreContentsButton =
    numberOfContentsToShow < contents.length ? (
      <Box float="right">
        <div onClick={handleShowMoreClick}>
          <Box variant="p" display="inline" fontSize="body-s" color="text-body-secondary">
            <Link>{`Show ${numberOfContentsToAdd} more`}</Link>
          </Box>
        </div>
      </Box>
    ) : null;

  // only appear when selections are fully expanded, and size is larger than default number
  const showLessContentsButton =
    numberOfContentsToShow > UtilsApi.defaultNumberOfContentsToShow &&
    numberOfContentsToShow === contents.length ? (
      <Box float="right">
        <div onClick={handleShowLessClick}>
          <Box variant="p" display="inline" fontSize="body-s" color="text-body-secondary">
            <Link>{`Show less`}</Link>
          </Box>
        </div>
      </Box>
    ) : null;

  const publicationList = (
    <SortableList
      items={contentsToShow}
      keyExtractor={keyOfPublication}
      onReorder={onReorder}
      item={(pub, { setNodeRef, style }) => {
        // Flag duplicates so the selection can show its inline alert.
        const isDuplicated = (contentCountMap.get(pub.contentId) ?? 0) > 1;
        return (
          <div ref={setNodeRef} style={style}>
            <PublicationSelection
              contentNameMap={contentNameMap}
              allPubs={allPubs}
              selectedPub={pub}
              handleSelectionChanged={onSelectionChanged}
              handleSelectionDeleted={onDeleteSelection}
              duplicationAlert={isDuplicated ? 'This publication already exists.' : ''}
            />
          </div>
        );
      }}
    />
  );

  const onAddPublication = () => {
    if (contents.length >= UtilsApi.publicationLimit) {
      setAlert(`You can only add up to ${UtilsApi.publicationLimit} publications.`);
    } else {
      setAlert('');
      const newId = latestId + 1;
      const newPub: PublicationItem = { contentId: '', id: newId };
      const newPubs = [...contents];
      newPubs.push(newPub);
      setContents(newPubs);
      handleSelectionsChanged(newPubs);
      setLatestId(newId);
      handleLastIdUpdated(newId);
      // expand all when adding new publication so that user can view all selected publications
      setContentsToShow(newPubs);
      setNumberOfContentsToShow(newPubs.length);
    }
  };

  const onDismissAlert = () => {
    setAlert('');
  };

  const alertSection = alert ? (
    <Alert onDismiss={onDismissAlert} dismissible dismissAriaLabel="Close alert" type="warning">
      {alert}
    </Alert>
  ) : null;

  const addButtonText =
    publicationType === PublicationType.AuthoredContent
      ? 'Add authored content'
      : 'Add reviewed content';
  const addButtonDisabled = contents.length >= allPubs.length;

  const addButton = (
    <Button onClick={onAddPublication} disabled={addButtonDisabled}>
      {addButtonText}
    </Button>
  );

  return (
    <SpaceBetween direction="vertical" size="s">
      <div>{publicationList}</div>
      <ColumnLayout columns={2}>
        {showMoreContentsButton}
        {showLessContentsButton}
      </ColumnLayout>
      {alertSection}
      {addButton}
    </SpaceBetween>
  );
};
